import React from "react";
import { Box } from "@mui/material";
import BaseModal from "../modals/BaseModal";
import ViolinPlot from "./ViolinPlot";
import ScatterPlot from "./ScatterPlot";
import HistogramPlot from "./HistogramPlot";
import BarChart from "./BarChart";
import DotPlot from "./DotPlot";

export interface PlotModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  plotType: "violin" | "scatter" | "histogram" | "bar" | "dot" | "custom";
  plotProps: {
    data?: any[];
    metric?: string;
    xMetric?: string;
    yMetric?: string;
    sizeMetric?: string;
    colorMetric?: string;
    defaultBins?: number;
    [key: string]: any;
  };
  defaultColor?: string;
  defaultSize?: number;
  children?: React.ReactNode;
}

const PlotModal: React.FC<PlotModalProps> = ({
  open,
  onClose,
  title,
  plotType,
  plotProps,
  defaultColor = "#19C1DB",
  defaultSize = 8,
  children,
}) => {
  // Function to render the appropriate plot component in the modal
  // To add a new plot type:
  // 1. Import the plot component at the top
  // 2. Add the plot type to the plotType union type
  // 3. Add the necessary props to plotProps interface
  // 4. Add a new condition in this function
  // 5. Update the plot component to pass plotType and plotProps to BasePlot
  const renderModalPlot = () => {
    if (plotType === "violin" && plotProps.data) {
      return (
        <ViolinPlot
          data={plotProps.data}
          metric={plotProps.metric}
          title="" // Empty title to avoid duplication with modal header
          defaultColor={defaultColor}
          className="w-full h-full"
          isFullscreen={true}
        />
      );
    }

    if (plotType === "scatter" && plotProps.data) {
      return (
        <ScatterPlot
          data={plotProps.data}
          xMetric={plotProps.xMetric}
          yMetric={plotProps.yMetric}
          title="" // Empty title to avoid duplication with modal header
          defaultColor={defaultColor}
          defaultSize={defaultSize}
          className="w-full h-full"
          isFullscreen={true}
        />
      );
    }

    if (plotType === "histogram" && plotProps.data) {
      return (
        <HistogramPlot
          data={plotProps.data}
          metric={plotProps.metric}
          title="" // Empty title to avoid duplication with modal header
          defaultColor={defaultColor}
          defaultBins={plotProps.defaultBins}
          className="w-full h-full"
          isFullscreen={true}
        />
      );
    }

    if (plotType === "bar" && plotProps.data) {
      return (
        <BarChart
          data={plotProps.data}
          title="" // Empty title to avoid duplication with modal header
          className="w-full h-full"
          isFullscreen={true}
        />
      );
    }

    if (plotType === "dot" && plotProps.data) {
      return (
        <DotPlot
          data={plotProps.data}
          xMetric={plotProps.xMetric}
          yMetric={plotProps.yMetric}
          sizeMetric={plotProps.sizeMetric}
          colorMetric={plotProps.colorMetric}
          title="" // Empty title to avoid duplication with modal header
          className="w-full h-full"
          isFullscreen={true}
        />
      );
    }

    // Fallback to children for custom plots
    return children;
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="xl"
      fullWidth={true}
      fullScreen={true}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          position: "relative",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {renderModalPlot()}
      </Box>
    </BaseModal>
  );
};

export default PlotModal;
